import React, { useRef, useState } from 'react';
import ChatArea from 'components/ChatArea';
import ChatInput from 'components/ChatInput';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getConversation, getConversationMessages } from 'api/conversations';
import { chat, createIdea } from 'api/companyMapping';
import { useFeedback } from 'contexts/feedback';
import { getCategoryLabel } from 'config/ideaCategories';

export default function CompanyMappingChat() {
    const { conversationId } = useParams();

    const [sticky, setSticky] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);

    const form = useRef();
    const cache = useQueryClient();
    const { setSuccess, setError } = useFeedback();

    const { isLoading: isConversationLoading } = useQuery({
        queryKey: ['conversations', conversationId],
        queryFn: async () => await getConversation({ id: conversationId }),
        enabled: !!conversationId,
    });

    const { data: messages = [], isLoading: isMessagesLoading } = useQuery({
        queryKey: ['messages', conversationId],
        queryFn: async () => await getConversationMessages({ id: conversationId }),
        enabled: !!conversationId,
    });

    // Create idea mutation (for AI-suggested ideas)
    const { mutate: createIdeaMutate } = useMutation({
        mutationKey: ['createCompanyMappingIdea', conversationId],
        mutationFn: createIdea,
        onSuccess: () => {
            cache.invalidateQueries(['company-mapping-ideas', conversationId]);
            setSuccess('Idea aggiunta con successo');
        },
        onError: () => {
            setError('Errore durante l\'aggiunta dell\'idea');
        }
    });

    const handleAddIdea = (ideaData) => {
        const newIdea = {
            ...ideaData,
            categoryLabel: getCategoryLabel(ideaData.category),
            source: 'ai',
            conversation: conversationId
        };

        createIdeaMutate({ conversationId, body: newIdea });
    };

    const { mutate: sendMessage, status: sendMessageStatus } = useMutation({
        mutationKey: ['company-mapping-chat', conversationId],
        mutationFn: ({ message }) => chat({
            conversationId,
            body: { message }
        }),
        onSuccess: (data) => {
            cache.invalidateQueries(['messages', conversationId]);
            cache.invalidateQueries(['conversations', conversationId]);
            cache.invalidateQueries(['company-mapping-ideas', conversationId]);
            setLastMessage(null);

            // Check if response contains an idea to add
            if (data?.idea) {
                handleAddIdea(data.idea);
            }
        },
        onError: (error) => {
            console.error("Error sending message:", error);
            setLastMessage(null);
        }
    });

    const onMessageSending = ({ message }) => {
        setLastMessage({
            _id: 'temp-id',
            content: message,
            role: 'user',
            you: true,
        });
    };

    const onMessageSubmit = (formData) => {
        const message = formData.get('message');
        onMessageSending({ message });
        sendMessage({ message });
    };

    const displayMessages = lastMessage ? [...messages, lastMessage] : messages;

    const isLoading = isConversationLoading || isMessagesLoading;

    return (
        <div className="relative flex flex-col h-full bg-white">
            <ChatArea
                messages={displayMessages}
                setSticky={setSticky}
                sticky={sticky}
                isThinking={isLoading || sendMessageStatus === 'pending'}
            />

            <ChatInput
                onMessageSubmit={onMessageSubmit}
                form={form}
            />
        </div>
    );
}
